<template>
  <div class="space-y-5">

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <div class="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <p class="text-xs text-gray-500 mb-1">Total</p>
        <p class="text-2xl font-bold text-white">{{ totalFiltered }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-green-800/40">
        <p class="text-xs text-green-500 mb-1">Vitórias</p>
        <p class="text-2xl font-bold text-green-400">{{ winsCount }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-red-800/40">
        <p class="text-xs text-red-500 mb-1">Derrotas</p>
        <p class="text-2xl font-bold text-red-400">{{ lossesCount }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-gray-800" :class="netProfit >= 0 ? 'border-green-800/40' : 'border-red-800/40'">
        <p class="text-xs text-gray-500 mb-1">Lucro Líquido</p>
        <p class="text-2xl font-bold" :class="netProfit >= 0 ? 'text-green-400' : 'text-red-400'">
          {{ netProfit >= 0 ? '+' : '' }}${{ Math.abs(netProfit).toFixed(2) }}
        </p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <p class="text-xs text-gray-500 mb-1">Win Rate</p>
        <p class="text-2xl font-bold text-blue-400">{{ winRate }}%</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-gray-900 rounded-2xl p-4 border border-gray-800">
      <div class="flex flex-wrap gap-3 items-end">
        <!-- Tier filter -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Nível de Risco</label>
          <div class="flex gap-1.5">
            <button
              v-for="t in tierOptions" :key="t.value"
              @click="filterTier = filterTier === t.value ? '' : t.value"
              class="px-3 py-1.5 rounded-lg text-xs font-bold border transition-all"
              :class="filterTier === t.value ? t.activeClass : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'"
            >{{ t.label }}</button>
          </div>
        </div>
        <!-- Result filter -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Resultado</label>
          <div class="flex gap-1.5">
            <button
              v-for="r in resultOptions" :key="r.value"
              @click="filterResult = filterResult === r.value ? '' : r.value"
              class="px-3 py-1.5 rounded-lg text-xs font-bold border transition-all"
              :class="filterResult === r.value ? r.activeClass : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'"
            >{{ r.label }}</button>
          </div>
        </div>
        <!-- Date range -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">De</label>
          <input v-model="dateFrom" type="date"
            class="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Até</label>
          <input v-model="dateTo" type="date"
            class="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50" />
        </div>
        <!-- Search -->
        <div class="flex-1 min-w-[160px]">
          <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Buscar</label>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Mercado, ID..."
            class="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50"
          />
        </div>
        <div class="flex gap-2 ml-auto">
          <button @click="clearFilters" class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-xs font-medium text-gray-400 transition-all">
            Limpar
          </button>
          <button @click="exportTrades" class="px-4 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-lg text-xs font-semibold transition-all">
            Exportar CSV
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-gray-800 bg-gray-800/30">
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Data/Hora</th>
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Mercado</th>
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tamanho</th>
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Preço</th>
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Nível IA</th>
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Resultado</th>
              <th class="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">P&L</th>
              <th class="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="pagedTrades.length === 0">
              <td colspan="8" class="px-6 py-12 text-center">
                <span class="text-3xl block mb-2">📋</span>
                <p class="text-gray-500 text-sm">Nenhum trade encontrado</p>
              </td>
            </tr>
            <tr
              v-for="trade in pagedTrades"
              :key="trade.id"
              class="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors cursor-pointer"
              @click="viewTradeDetails(trade)"
            >
              <td class="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">{{ formatDate(trade.timestamp) }}</td>
              <td class="px-5 py-3 max-w-[220px]">
                <p class="text-sm text-white truncate">{{ trade.question || trade.marketId }}</p>
                <p class="text-xs text-gray-600 font-mono truncate">{{ trade.marketId?.substring(0, 20) }}…</p>
              </td>
              <td class="px-5 py-3 text-sm text-white font-medium whitespace-nowrap">{{ trade.size }} USDC</td>
              <td class="px-5 py-3 text-sm text-gray-300">{{ trade.price || 'N/A' }}</td>
              <td class="px-5 py-3">
                <div class="flex flex-col gap-1">
                  <span class="px-2 py-0.5 rounded text-xs font-bold w-fit" :class="tierBadgeClass(trade.analysisConfidence)">
                    {{ getTier(trade.analysisConfidence) }}
                  </span>
                  <span class="text-xs text-gray-500">{{ trade.analysisConfidence }}%</span>
                </div>
              </td>
              <td class="px-5 py-3">
                <span class="px-2.5 py-1 rounded-lg text-xs font-bold" :class="resultClass(trade.result)">
                  {{ trade.result }}
                </span>
              </td>
              <td class="px-5 py-3 text-sm font-medium" :class="tradePnl(trade) >= 0 ? 'text-green-400' : 'text-red-400'">
                <span v-if="trade.result !== 'PENDING'">
                  {{ tradePnl(trade) >= 0 ? '+' : '' }}${{ Math.abs(tradePnl(trade)).toFixed(2) }}
                </span>
                <span v-else class="text-gray-600">—</span>
              </td>
              <td class="px-5 py-3 text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap" @click.stop="viewTradeDetails(trade)">
                Ver detalhes
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div class="flex justify-between items-center">
      <p class="text-xs text-gray-500">
        {{ totalFiltered > 0 ? (currentPage - 1) * pageSize + 1 : 0 }}–{{ Math.min(currentPage * pageSize, totalFiltered) }} de {{ totalFiltered }} trades
      </p>
      <div class="flex items-center gap-1.5">
        <button @click="currentPage = 1" :disabled="currentPage === 1" class="px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">««</button>
        <button @click="currentPage--" :disabled="currentPage === 1" class="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">‹ Anterior</button>
        <span class="px-3 py-1.5 text-xs text-gray-400 bg-gray-800/50 rounded-lg">{{ currentPage }} / {{ totalPages }}</span>
        <button @click="currentPage++" :disabled="currentPage >= totalPages" class="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">Próxima ›</button>
        <button @click="currentPage = totalPages" :disabled="currentPage >= totalPages" class="px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">»»</button>
      </div>
    </div>

    <!-- Trade Detail Modal -->
    <teleport to="body">
      <div v-if="selectedTrade" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" @click.self="selectedTrade = null">
        <div class="bg-gray-900 rounded-2xl p-6 max-w-lg w-full mx-4 border border-gray-700 shadow-2xl">
          <div class="flex items-center justify-between mb-5">
            <h3 class="text-base font-bold text-white">Detalhes do Trade</h3>
            <button @click="selectedTrade = null" class="text-gray-400 hover:text-white transition-colors text-xl leading-none">&times;</button>
          </div>
          <div class="space-y-0">
            <div v-for="row in tradeDetailRows" :key="row.label" class="flex justify-between items-center py-2.5 border-b border-gray-800/80 last:border-0">
              <span class="text-xs text-gray-500 uppercase tracking-wider">{{ row.label }}</span>
              <span v-if="!row.slot" class="text-sm font-medium text-white text-right max-w-[260px] break-all">{{ row.value }}</span>
              <span v-else-if="row.slot === 'tier'" class="px-2.5 py-0.5 rounded text-xs font-bold" :class="tierBadgeClass(selectedTrade.analysisConfidence)">
                {{ getTier(selectedTrade.analysisConfidence) }} ({{ selectedTrade.analysisConfidence }}%)
              </span>
              <span v-else-if="row.slot === 'result'" class="px-2.5 py-0.5 rounded-lg text-xs font-bold" :class="resultClass(selectedTrade.result)">{{ selectedTrade.result }}</span>
            </div>
          </div>
          <div v-if="selectedTrade.reasoning" class="mt-4 bg-gray-800/60 rounded-xl p-3 border border-gray-700/50">
            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Raciocínio IA</p>
            <p class="text-sm text-gray-300 leading-relaxed">{{ selectedTrade.reasoning }}</p>
          </div>
          <button @click="selectedTrade = null" class="w-full mt-5 bg-gray-800 hover:bg-gray-700 border border-gray-700 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all">Fechar</button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { getApiBaseUrl } from '../config/runtime';
import { useAuthStore } from '../stores/authStore';

const authStore = useAuthStore();

const trades = ref([]);
const filterTier = ref('');
const filterResult = ref('');
const searchQuery = ref('');
const dateFrom = ref('');
const dateTo = ref('');
const currentPage = ref(1);
const pageSize = 15;
const selectedTrade = ref(null);
let refreshInterval = null;

const tierOptions = [
  { value: 'CONSERVADOR', label: 'Conservador', activeClass: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { value: 'MODERADO',    label: 'Moderado',    activeClass: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  { value: 'EXTREMO',     label: 'Extremo',     activeClass: 'bg-red-500/20 text-red-400 border-red-500/40' },
];

const resultOptions = [
  { value: 'WIN',     label: 'Vitória',  activeClass: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { value: 'LOSS',    label: 'Derrota',  activeClass: 'bg-red-500/20 text-red-400 border-red-500/40' },
  { value: 'PENDING', label: 'Pendente', activeClass: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
];

function getTier(conf) {
  if ((conf || 0) >= 70) return 'CONSERVADOR';
  if ((conf || 0) >= 60) return 'MODERADO';
  return 'EXTREMO';
}

function tierBadgeClass(conf) {
  const t = getTier(conf);
  if (t === 'CONSERVADOR') return 'bg-green-500/20 text-green-400';
  if (t === 'MODERADO')    return 'bg-yellow-500/20 text-yellow-400';
  return 'bg-red-500/20 text-red-400';
}

function resultClass(r) {
  if (r === 'WIN')     return 'bg-green-500/20 text-green-400';
  if (r === 'LOSS')    return 'bg-red-500/20 text-red-400';
  return 'bg-yellow-500/20 text-yellow-400';
}

function tradePnl(trade) {
  if (trade.profit !== undefined) return Number(trade.profit);
  if (trade.result === 'WIN')  return Number(trade.size || 0) * 0.9;
  if (trade.result === 'LOSS') return -Number(trade.size || 0);
  return 0;
}

const allFiltered = computed(() => {
  return trades.value.filter(t => {
    if (filterTier.value && getTier(t.analysisConfidence) !== filterTier.value) return false;
    if (filterResult.value && t.result !== filterResult.value) return false;
    if (dateFrom.value) {
      if (!t.timestamp || t.timestamp.substring(0, 10) < dateFrom.value) return false;
    }
    if (dateTo.value) {
      if (!t.timestamp || t.timestamp.substring(0, 10) > dateTo.value) return false;
    }
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase();
      if (!((t.question || '').toLowerCase().includes(q) || (t.marketId || '').toLowerCase().includes(q))) return false;
    }
    return true;
  });
});

const totalFiltered = computed(() => allFiltered.value.length);
const totalPages    = computed(() => Math.max(1, Math.ceil(totalFiltered.value / pageSize)));
const pagedTrades   = computed(() => allFiltered.value.slice((currentPage.value - 1) * pageSize, currentPage.value * pageSize));

const winsCount  = computed(() => allFiltered.value.filter(t => t.result === 'WIN').length);
const lossesCount = computed(() => allFiltered.value.filter(t => t.result === 'LOSS').length);
const netProfit  = computed(() => allFiltered.value.reduce((s, t) => s + tradePnl(t), 0));
const winRate    = computed(() => {
  const decided = winsCount.value + lossesCount.value;
  return decided > 0 ? ((winsCount.value / decided) * 100).toFixed(1) : '0.0';
});

watch([filterTier, filterResult, searchQuery, dateFrom, dateTo], () => { currentPage.value = 1; });

const tradeDetailRows = computed(() => {
  if (!selectedTrade.value) return [];
  const t = selectedTrade.value;
  return [
    { label: 'ID',        value: t.id },
    { label: 'Data',      value: formatDate(t.timestamp) },
    { label: 'Mercado',   value: t.question || t.marketId },
    { label: 'Tamanho',   value: `${t.size} USDC` },
    { label: 'Preço',     value: t.price || 'N/A' },
    { label: 'Nível IA',  slot: 'tier' },
    { label: 'Resultado', slot: 'result' },
  ];
});

const formatDate = (d) => new Date(d).toLocaleString('pt-BR');

function clearFilters() {
  filterTier.value = '';
  filterResult.value = '';
  searchQuery.value = '';
  dateFrom.value = '';
  dateTo.value = '';
}

function viewTradeDetails(trade) {
  selectedTrade.value = trade;
}

function exportTrades() {
  const rows = [
    ['Data/Hora', 'Mercado', 'Tamanho', 'Preço', 'Nível', 'Confiança%', 'Resultado', 'P&L'],
    ...allFiltered.value.map(t => [
      formatDate(t.timestamp),
      t.question || t.marketId,
      t.size,
      t.price || 'N/A',
      getTier(t.analysisConfidence),
      t.analysisConfidence,
      t.result,
      tradePnl(t).toFixed(2),
    ]),
  ].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([rows], { type: 'text/csv' })),
    download: `trades_${new Date().toISOString().split('T')[0]}.csv`,
  });
  a.click();
}

async function fetchTrades() {
  try {
    const headers = authStore.token
      ? { Authorization: `Bearer ${authStore.token}` }
      : { 'X-API-Key': '' };
    const res = await fetch(`${getApiBaseUrl()}/trades?limit=2000`, { headers });
    if (res.ok) {
      const data = await res.json();
      trades.value = (data.trades || []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  } catch (e) {
    console.error('Erro ao buscar trades:', e);
  }
}

onMounted(() => {
  fetchTrades();
  refreshInterval = setInterval(fetchTrades, 30000);
});

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});
</script>
